/**
 * Конфигурация уровней реферальных наград (Веха 9, ARCHITECTURE_PLAN §17 фаза 2).
 *
 * Единственный источник правды для порогов, бейджей и тем. Используется:
 *   - `apps/api/src/services/reward.ts` (computeUserRewards, syncReward)
 *   - `apps/miniapp` (отображение прогресса, unlock-проверка форматов)
 *
 * Пороги выбраны под максимальную виральность (см. продуктовые решения сессии
 * 2026-05-29): первый порог = 1 (мгновенная валидация петли), затем 5/15/40.
 */

export type RewardBadge = 'viral_starter' | 'storyteller' | 'loop_master';
export type RewardTheme = 'confession' | 'guesswho';

export type RewardLevelConfig = {
  readonly level: number;
  readonly referralsNeeded: number;
  readonly badges: readonly RewardBadge[];
  readonly themes: readonly RewardTheme[];
};

export const REWARD_LEVELS: readonly RewardLevelConfig[] = [
  { level: 0, referralsNeeded: 0,  badges: [],                themes: [] },
  { level: 1, referralsNeeded: 1,  badges: ['viral_starter'], themes: [] },
  { level: 2, referralsNeeded: 5,  badges: [],                themes: ['confession'] },
  { level: 3, referralsNeeded: 15, badges: ['storyteller'],   themes: ['guesswho'] },
  { level: 4, referralsNeeded: 40, badges: ['loop_master'],   themes: [] },
];

/**
 * Вычислить уровень наград по числу confirmed-рефералов.
 * Возвращает наивысший уровень, порог которого не превышает `confirmedReferrals`.
 */
export function computeRewardLevel(confirmedReferrals: number): number {
  let level = 0;
  for (const cfg of REWARD_LEVELS) {
    if (confirmedReferrals >= cfg.referralsNeeded) level = cfg.level;
  }
  return level;
}

/**
 * Накопленные разблокировки для данного уровня (все уровни ≤ level включительно).
 * Возвращает дедуплицированные массивы бейджей и тем.
 */
export function accumulateUnlocks(level: number): {
  badges: RewardBadge[];
  themes: RewardTheme[];
} {
  const badges = new Set<RewardBadge>();
  const themes = new Set<RewardTheme>();
  for (const cfg of REWARD_LEVELS) {
    if (cfg.level <= level) {
      for (const b of cfg.badges) badges.add(b);
      for (const t of cfg.themes) themes.add(t);
    }
  }
  return { badges: [...badges], themes: [...themes] };
}

/**
 * Порог следующего уровня или null, если юзер уже на максимальном уровне.
 */
export function nextLevelThreshold(currentLevel: number): number | null {
  const next = REWARD_LEVELS.find((cfg) => cfg.level === currentLevel + 1);
  return next?.referralsNeeded ?? null;
}

/**
 * Velocity anti-fraud тюнинг (work-032, [[project_activation_funnel_gap]]).
 *
 * Единственный источник velocity-РЕШЕНИЯ для ОБОИХ писателей `Referral`
 * (`apps/api/src/services/referral.ts` — путь `?startapp=`, и
 * `apps/bot/src/services/referral.ts` — bot-first `?start=` fallback), чтобы
 * лимит и статус не разъехались между каналами.
 */

/** Максимум confirmed/quarantined рефералов с одного source box за 24h. */
export const REFERRAL_VELOCITY_LIMIT = 10;
export const VELOCITY_WINDOW_MS = 24 * 60 * 60 * 1000;

/**
 * Pure-решение по числу рефералов source box за последние 24h:
 * `recentCount >= REFERRAL_VELOCITY_LIMIT` → 'quarantined' (без наград),
 * иначе → 'confirmed'.
 */
export function referralStatusForRecentCount(
  recentCount: number,
): { status: 'confirmed' | 'quarantined'; confirmed: boolean } {
  if (recentCount >= REFERRAL_VELOCITY_LIMIT) {
    return { status: 'quarantined', confirmed: false };
  }
  return { status: 'confirmed', confirmed: true };
}

/**
 * Referral-атрибуция: «первый бокс» больше не годный критерий (work-034,
 * [[project_activation_funnel_gap]]) — рекламный трафик bot-first, ко времени
 * перехода по чужой ссылке у юзера уже есть авто-созданный бокс из `/start`
 * (`ensureUserAndBox`/`ensureUser` в `apps/bot`/`apps/api`), поэтому старый
 * гейт `created && ownedCount===1` был false почти всегда → 0 атрибуций в
 * проде (27 попыток, все `existing_author`).
 *
 * Новый критерий — «referrable newcomer»: у юзера НЕТ авторского вовлечения
 * (0 полученных вопросов, 0 написанных ответов) И аккаунт создан недавно
 * (`accountAgeMs < REFERRAL_NEWCOMER_WINDOW_MS`). Пустой авто-созданный бокс
 * САМ ПО СЕБЕ не считается «быть автором» — но давний аккаунт с вечно пустым
 * боксом не должен атрибутироваться на каждый переход по чужой ссылке
 * (recency-окно закрывает эту дыру, ровно тот блокер, что вернул
 * plan-verifier).
 *
 * Единственный источник для ОБОИХ писателей `Referral`
 * (`apps/api/src/routes/boxes.ts`, `apps/bot/src/handlers/start.ts`), чтобы
 * критерий не разъехался между каналами.
 */

/** Окно «недавно присоединился» для referral-атрибуции — 7 дней. */
export const REFERRAL_NEWCOMER_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Pure-решение: является ли юзер «referrable newcomer» на момент запроса.
 * `accountAgeMs === REFERRAL_NEWCOMER_WINDOW_MS` (граница) → false (строгое `<`).
 */
export function isReferrableNewcomer(opts: {
  questionsReceived: number;
  answersWritten: number;
  accountAgeMs: number;
}): boolean {
  return (
    opts.questionsReceived === 0 &&
    opts.answersWritten === 0 &&
    opts.accountAgeMs < REFERRAL_NEWCOMER_WINDOW_MS
  );
}
